/**
 * Back-up van alle persisted store-data als .json (export + import-validatie).
 *
 * De export verpakt exact de tien gepersisteerde velden (zie partialize in
 * store/useRoosterStore.js) in een omhulsel met app-naam, versie en datum. De
 * download hergebruikt het Blob/URL.createObjectURL-patroon uit
 * lib/exportMaandroosterExcel.js. Het terugzetten van de data gebeurt in de
 * store-actie importBackup; hier zitten alleen het bouwen, downloaden en
 * valideren.
 */

import { logError } from './logger.js';
import { vandaagNL } from './tijd.js';
import { leesActieveOmgeving, STANDAARD_OMGEVING } from './omgeving.js';

// Versie van het back-up-formaat; moet overeenkomen met de persist-version (3).
export const BACKUP_VERSION = 3;

// De gepersisteerde velden (1-op-1 met partialize in useRoosterStore.js).
export const BACKUP_VELDEN = [
  'weeknummer',
  'jaar',
  'afwezigheid',
  'vakanties',
  'bijzonderheden',
  'celOverrides',
  // Afgevinkte meldingen uit het paneel boven de matrix — client-eigen en teambreed, dus
  // dezelfde route als celOverrides: hij hoort in de back-up én in de PUT-payload.
  'afgevinkteMeldingen',
  'geselecteerdeMedewerker',
  'regels',
  'vastgelegdeWeken',
  'maandroosters',
];

/** Bouwt het back-up-omhulsel uit een store-state-achtig object. */
export function bouwBackupBestand(state) {
  const data = {};
  for (const veld of BACKUP_VELDEN) data[veld] = state[veld];
  // Welke omgeving deze platte velden voorstellen — zodat PUT /api/data ALLEEN díe omgeving
  // overschrijft. De store is nog plat (store-refactor is stap 6); dit is de routering-hint.
  data.actieveOmgeving = leesActieveOmgeving();
  return {
    app: 'sass-rooster',
    version: BACKUP_VERSION,
    exportDatum: new Date().toISOString(),
    data,
  };
}

/**
 * Downloadt de back-up als .json. `state` is useRoosterStore.getState().
 * Retourneert true bij succes, false bij een fout (die dan wordt gelogd).
 */
export function downloadBackup(state) {
  if (typeof document === 'undefined') return false;
  try {
    const bestand = bouwBackupBestand(state);
    const inhoud = JSON.stringify(bestand, null, 2);
    const blob = new Blob([inhoud], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    // vandaagNL, niet toISOString: die schuift rond middernacht een dag terug — zie lib/tijd.js.
    const datum = vandaagNL();
    // De omgeving hoort in de NAAM: het bestand bevat maar één lade, en twee exports op
    // dezelfde dag uit verschillende omgevingen kregen anders dezelfde naam. De SLEUTEL,
    // niet de displaynaam — die is stabiel. Geen keuze opgeslagen -> dezelfde terugval als
    // de store-bucket, zodat de naam de lade noemt waar de data echt uit komt.
    const omgeving = leesActieveOmgeving() || STANDAARD_OMGEVING;
    const a = document.createElement('a');
    a.href = url;
    a.download = `SASS_Rooster_Backup_${omgeving}_${datum}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    logError(err, { source: 'export', feature: 'backup-json' });
    return false;
  }
}

/**
 * Valideert een geparset back-up-object. Retourneert { ok, fout, mismatch }: bij ok=false
 * bevat `fout` een NL-melding die met window.alert getoond kan worden.
 *
 * `mismatch` is GEEN afkeuring maar een waarschuwing: importBackup schrijft naar de ACTIEVE
 * omgeving, niet naar de omgeving die in het bestand zit. Een Maastricht-export terugzetten
 * terwijl Arnhem actief is, overschrijft dus stil Arnhem. Bewust doen mag; per ongeluk niet,
 * dus de aanroeper laat de gebruiker bevestigen. Is er geen verschil, dan is mismatch null.
 *
 * @param {object} parsed - het geparste back-up-object.
 * @param {string|null} [actieveOmgeving] - leesActieveOmgeving(); weggelaten -> geen check.
 */
export function valideerBackup(parsed, actieveOmgeving) {
  if (!parsed || typeof parsed !== 'object' || typeof parsed.data !== 'object' || parsed.data === null) {
    return { ok: false, fout: 'Ongeldig back-up-bestand: geen geldige back-up-structuur.', mismatch: null };
  }
  if (parsed.version !== BACKUP_VERSION) {
    return {
      ok: false,
      fout: `Back-up-versie komt niet overeen (verwacht versie ${BACKUP_VERSION}, bestand heeft versie ${parsed.version}).`,
      mismatch: null,
    };
  }
  const regels = parsed.data.regels;
  if (!regels || typeof regels !== 'object' || !regels.medewerkers || typeof regels.medewerkers !== 'object') {
    return { ok: false, fout: 'Ongeldig back-up-bestand: medewerkers-data ontbreekt.', mismatch: null };
  }

  // Beide kanten door dezelfde terugval: een oude export heeft actieveOmgeving null, en ook
  // aan deze kant kan er nog geen keuze opgeslagen zijn. Zonder die terugval zou een export
  // uit de standaard-lade tegen een null vergeleken worden en vals alarm geven.
  let mismatch = null;
  if (actieveOmgeving !== undefined) {
    const inBestand = parsed.data.actieveOmgeving || STANDAARD_OMGEVING;
    const nuActief = actieveOmgeving || STANDAARD_OMGEVING;
    if (inBestand !== nuActief) mismatch = { bestand: inBestand, actief: nuActief };
  }
  return { ok: true, fout: null, mismatch };
}
